import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import {Component, Input, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ToasterService} from 'angular2-toaster';
import {DataService} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {KobeacService} from './kobeac.service';
import {TranslocoService} from '@jsverse/transloco';

interface LedgerRow {
    invoiceRid: string;   // AC_Invoice の @rid (請求が無いファイルのみ行では空)
    status: string;       // AC_Invoice.status (編集可能)
    total: number | null; // AC_Invoice.total
    customerId: any;
    customerName: string;
    invoiceReg: boolean;  // 顧客.インボイス登録
    regNo: string;        // 顧客.登録番号
    payment: string;      // 顧客.支払方法
    fax: boolean;         // 顧客.FAX
    uuid: string;
    auctionDate: string;
    fileName: string;
    size: number;
    created: number;
    modified: number;
    sizeText: string;
    createdText: string;
    updatedText: string;
    checked: boolean;
}

/**
 * kobe-ac 売買台帳一覧 (issue #161, task #382/#383/#409)。管理者向け。
 * オークション単位で AC_Invoice (顧客×開催) を読み、顧客フォルダの売買台帳PDF
 * (ファイル情報) と突合して 1 行=1 請求で一覧する。顧客ID昇順で初期表示し、
 * ステータス列 (AC_Invoice.status) を直接編集・保存できる。単体/複数(ZIP)
 * ダウンロードとオークション指定の一括生成も従来どおり。
 *
 * <p>オークション未指定の単独ルート (フィルタ「すべて」) では、従来どおり全顧客の
 * PDF をファイル走査で一覧する (請求列は空)。一覧・生成・DL・ステータス更新は
 * backend でも管理者ロールを検証する。
 */
@Component({
    standalone: false,
    selector: 'kobeac-ledger-list',
    templateUrl: 'ledger-list.component.html',
})
export class KobeacLedgerListComponent implements OnInit {
    private base = ActivePromotionConfig.SERVER_URL + '/rest/kobeac/ledger';
    private rows: LedgerRow[] = [];
    cv: wjcCore.CollectionView = new wjcCore.CollectionView([], {pageSize: 100});
    pageSize = 100;
    loading = false;
    generating = false;

    /** 表示件数メニューの選択肢 (menu.selectedIndex と対応)。 */
    private readonly PAGE_SIZES = [100, 300, 1000];

    /** 請求区分 (ステータス) 列のドロップダウン候補。AC_Master(category='請求区分') から作る。 */
    statusMap: wjcGrid.DataMap | null = null;

    filterDate = '';
    filterCustomer = '';
    /** 追加フィルタ: '' = すべて。 */
    filterInvoiceReg = '';   // '' | 'yes' | 'no'
    filterRegNo = '';        // '' | 'has' | 'none'
    filterPayment = '';      // '' | 支払方法の値
    filterFax = '';          // '' | 'yes' | 'no'
    /** 支払方法フィルタの候補 (AC_Master category='支払方法')。 */
    paymentOptions: string[] = [];

    auctions: any[] = [];
    genAuctionRid = '';
    genCreatedDate = ''; // 作成日 (yyyy-MM-dd)。空なら当日

    /** 全顧客フォルダの PDF (backend /list の生データ)。オークション行との突合に使う。 */
    private allFiles: any[] = [];
    private filesLoaded = false;
    /** 対象オークションの AC_Invoice を1行=1請求で表示中か (= オークション指定あり)。 */
    private invoiceMode = false;

    /** 統合画面 (kobeac-ledger-home) 内に埋め込むと true。自前のオークション選択を隠し、
     *  共有日付 (auctionRid) で対象オークションを受け取る。単独ルートでは false のまま。 */
    @Input() embedded = false;
    /** 親から受けたオークション (@rid)。auctions ロード前に届くこともあるため生の rid を保持する。 */
    private _auctionRid = '';

    @Input() set auctionRid(rid: string) {
        this._auctionRid = rid || '';
        this.genAuctionRid = this._auctionRid;
        this.reload();
    }

    constructor(private http: HttpClient, private dataService: DataService,
                private kobeac: KobeacService, private toasterService: ToasterService,
                private transloco: TranslocoService) {
    }

    async ngOnInit() {
        await this.kobeac.ensureLoaded();
        this.auctions = this.kobeac.auctions;
        this.loadStatusMap();
        this.loadPaymentOptions();
        this.reload();
    }

    /** 支払方法フィルタの候補を AC_Master(category='支払方法') から読み込む。 */
    private loadPaymentOptions(): void {
        const sql = "SELECT value FROM AC_Master WHERE category = '支払方法' ORDER BY sort";
        this.dataService.query({sql: sql, count: sql, fetchPlan: '*:0'}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: (res: any) => {
                const opts: string[] = [];
                if (wjcCore.isArray(res.results)) {
                    for (const r of res.results) {
                        const v = r && r.value != null ? String(r.value) : '';
                        if (v && opts.indexOf(v) < 0) opts.push(v);
                    }
                }
                this.paymentOptions = opts;
            },
            error: () => { this.paymentOptions = []; },
        });
    }

    /**
     * 請求区分ドロップダウンの候補を AC_Master(category='請求区分') から読み込む。
     * status は文字列そのものを保存するため、key=表示=value の文字列 DataMap を作る
     * (master.component の reportMap と同流儀)。マスタ未登録なら null=自由入力のまま。
     */
    private loadStatusMap(): void {
        const sql = "SELECT value FROM AC_Master WHERE category = '請求区分' ORDER BY sort";
        this.dataService.query({sql: sql, count: sql, fetchPlan: '*:0'}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: (res: any) => {
                const rows: { value: string }[] = [];
                if (wjcCore.isArray(res.results)) {
                    for (const r of res.results) {
                        const v = r && r.value != null ? String(r.value) : '';
                        if (v) rows.push({value: v});
                    }
                }
                this.statusMap = rows.length ? new wjcGrid.DataMap(rows, 'value', 'value') : null;
            },
            error: () => { this.statusMap = null; },
        });
    }

    auctionLabel(a: any): string {
        return this.kobeac.formatOrientDate(a['日付']);
    }

    /** 現在対象とするオークション (@rid)。埋め込みは共有日付、単独はオークション選択。 */
    private targetAuctionRid(): string {
        return this._auctionRid || this.genAuctionRid || '';
    }

    /** オークション (@rid) の開催日を yyyymmdd 数字に解決する。未ロード/未選択なら空。 */
    private digitsOf(rid: string): string {
        if (!rid) {
            return '';
        }
        const found = this.auctions.filter((a) => a['@rid'] === rid);
        return found.length ? String(found[0]['日付'] || '').replace(/\D/g, '').slice(0, 8) : '';
    }

    /** epoch millis を yyyy/MM/dd HH:mm に整形する (0/未設定は空)。 */
    private formatDateTime(millis: number): string {
        if (!millis || millis <= 0) {
            return '';
        }
        const d = new Date(millis);
        const p = (n: number) => (n < 10 ? '0' + n : '' + n);
        return d.getFullYear() + '/' + p(d.getMonth() + 1) + '/' + p(d.getDate())
            + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
    }

    /** 「更新」ボタン: ファイルを読み直してから再構築する。 */
    list(): void {
        this.filesLoaded = false;
        this.reload();
    }

    /** ファイル (未ロードなら取得) → オークションに応じて行を構築する。 */
    reload(): void {
        if (!this.filesLoaded) {
            this.loading = true;
            this.http.get(this.base + '/list').subscribe({
                next: (res: any) => {
                    this.allFiles = (res && res.files) ? res.files : [];
                    this.filesLoaded = true;
                    this.loading = false;
                    this.buildRows();
                },
                error: () => {
                    this.loading = false;
                    this.toasterService.pop('error', this.transloco.translate('kobeac.trade_ledger'), this.transloco.translate('kobeac.list_load_failed'));
                },
            });
        } else {
            this.buildRows();
        }
    }

    /** ファイル生データを {uuid, 日付数字} の LedgerRow 素材へ変換する。 */
    private fileRow(f: any): LedgerRow {
        return {
            invoiceRid: '',
            status: '',
            total: null,
            customerId: f.customerId,
            customerName: f.customerName,
            invoiceReg: false,
            regNo: '',
            payment: '',
            fax: false,
            uuid: f.uuid,
            auctionDate: f.auctionDate,
            fileName: f.fileName,
            size: f.size,
            created: f.created,
            modified: f.modified,
            sizeText: this.convertSize(f.size),
            createdText: this.formatDateTime(f.created),
            updatedText: this.formatDateTime(f.modified),
            checked: false,
        };
    }

    /** 顧客ID昇順 (数値優先、同値は顧客名) に並べる。 */
    private sortByCustomer(rows: LedgerRow[]): void {
        const num = (x: any) => {
            const n = parseInt(String(x == null ? '' : x).replace(/\D/g, ''), 10);
            return isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
        };
        rows.sort((a, b) => (num(a.customerId) - num(b.customerId)) ||
            String(a.customerName || '').localeCompare(String(b.customerName || '')));
    }

    private buildRows(): void {
        const rid = this.targetAuctionRid();
        if (!rid) {
            // オークション未指定 (単独ルートの「すべて」): 従来どおりファイル走査で全件表示。
            this.invoiceMode = false;
            this.rows = this.allFiles.map((f) => this.fileRow(f));
            this.sortByCustomer(this.rows);
            this.buildView();
            return;
        }
        // オークション指定あり: AC_Invoice を読み、ファイル情報を突合する (1行=1請求)。
        this.invoiceMode = true;
        const digits = this.digitsOf(rid);
        const sql = 'SELECT @rid as rid, customer.ID as customerId, customer.uuid as uuid, '
            + 'customerName, total, status, customer.`インボイス登録` as invoiceReg, '
            + 'customer.`登録番号` as regNo, customer.`支払方法` as payment, customer.`FAX` as fax '
            + 'FROM AC_Invoice WHERE auction = ' + rid;
        this.loading = true;
        this.dataService.query({sql: sql, count: sql, fetchPlan: '*:0'}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: (res: any) => {
                const invs = wjcCore.isArray(res.results) ? res.results : [];
                this.rows = invs.map((v: any) => {
                    const uuid = this.ridStr(v.uuid);
                    const f = this.allFiles.filter((x) => x.uuid === uuid &&
                        (!digits || (x.auctionDate || '').replace(/\D/g, '') === digits))[0];
                    return {
                        invoiceRid: this.ridStr(v.rid),
                        status: v.status == null ? '' : String(v.status),
                        total: v.total == null ? null : Number(v.total),
                        customerId: v.customerId,
                        customerName: v.customerName,
                        invoiceReg: v.invoiceReg === true,
                        regNo: v.regNo == null ? '' : String(v.regNo),
                        payment: v.payment == null ? '' : String(v.payment),
                        fax: v.fax === true,
                        uuid: uuid,
                        auctionDate: f ? f.auctionDate : '',
                        fileName: f ? f.fileName : '',
                        size: f ? f.size : 0,
                        created: f ? f.created : 0,
                        modified: f ? f.modified : 0,
                        sizeText: f ? this.convertSize(f.size) : '',
                        createdText: f ? this.formatDateTime(f.created) : '',
                        updatedText: f ? this.formatDateTime(f.modified) : '',
                        checked: false,
                    } as LedgerRow;
                });
                this.sortByCustomer(this.rows);
                this.buildView();
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.toasterService.pop('error', this.transloco.translate('kobeac.trade_ledger'), this.transloco.translate('kobeac.invoice_load_failed'));
            },
        });
    }

    private buildView(): void {
        const cv = new wjcCore.CollectionView(this.rows, {pageSize: this.pageSize});
        cv.filter = (item: LedgerRow) => {
            const c = this.filterCustomer.trim();
            // invoiceMode の行は対象オークションに絞り済み。単独「すべて」時のみ日付で絞る。
            const dateOk = this.invoiceMode || (!this.filterDate || item.auctionDate === this.filterDate);
            const custOk = !c || (item.customerName || '').indexOf(c) >= 0 || String(item.customerId).indexOf(c) >= 0;
            // 顧客属性フィルタ (インボイス登録 / 登録番号有無 / 支払方法 / FAX)
            const invOk = !this.filterInvoiceReg || (this.filterInvoiceReg === 'yes' ? item.invoiceReg : !item.invoiceReg);
            const hasReg = !!(item.regNo && item.regNo.trim());
            const regOk = !this.filterRegNo || (this.filterRegNo === 'has' ? hasReg : !hasReg);
            const payOk = !this.filterPayment || item.payment === this.filterPayment;
            const faxOk = !this.filterFax || (this.filterFax === 'yes' ? item.fax : !item.fax);
            return dateOk && custOk && invOk && regOk && payOk && faxOk;
        };
        this.cv = cv;
    }

    applyFilter(): void {
        if (this.cv) this.cv.refresh();
    }

    /**
     * 表示件数 (ページサイズ) 変更。wj-menu は value/valueChanged が不安定なため
     * (master.component の onMasterMenu と同様)、itemClicked 時に確定している
     * menu.selectedIndex を PAGE_SIZES に対応付けて解決し cv に反映する。
     */
    onPageSizeChanged(menu?: any): void {
        const idx = menu != null && wjcCore.isInt(menu.selectedIndex) ? menu.selectedIndex : -1;
        if (idx >= 0 && idx < this.PAGE_SIZES.length) {
            this.pageSize = this.PAGE_SIZES[idx];
        }
        if (this.cv) {
            this.cv.pageSize = this.pageSize;
        }
    }

    /**
     * ステータス列の編集確定。AC_Invoice.status を backend に保存する。
     * 請求の無い行 (ファイルのみ) は編集を戻す。
     */
    onCellEditEnded(grid: wjcGrid.FlexGrid, e: wjcGrid.CellEditEndingEventArgs): void {
        const col = grid.columns[e.col];
        if (!col || col.binding !== 'status') {
            return;
        }
        const row = grid.rows[e.row] ? (grid.rows[e.row].dataItem as LedgerRow) : null;
        if (!row) {
            return;
        }
        if (!row.invoiceRid) {
            this.toasterService.pop('warning', this.transloco.translate('kobeac.status'), this.transloco.translate('kobeac.no_invoice_for_row'));
            return;
        }
        const status = row.status == null ? '' : String(row.status);
        this.http.post(this.base + '/status', {invoice: row.invoiceRid, status: status}).subscribe({
            next: () => this.toasterService.pop('info', this.transloco.translate('kobeac.status'), this.transloco.translate('kobeac.updated_name', {name: row.customerName || ''})),
            error: () => this.toasterService.pop('error', this.transloco.translate('kobeac.status'), this.transloco.translate('kobeac.update_failed_period')),
        });
    }

    /** フィルタ用の重複なしオークション日 (降順) */
    get dates(): string[] {
        const set: { [k: string]: boolean } = {};
        for (const r of this.rows) if (r.auctionDate) set[r.auctionDate] = true;
        return Object.keys(set).sort().reverse();
    }

    get selectedCount(): number {
        return this.rows.filter((r) => r.checked && r.fileName).length;
    }

    /** 現在フィルタで見えている行 (全ページ) のうち PDF のある行を全選択/解除 */
    setAllChecked(checked: boolean): void {
        for (const r of this.cv.items as LedgerRow[]) if (r.fileName) r.checked = checked;
    }

    convertSize(size: number): string {
        return size < 1024000 ? (size / 1024).toFixed(1) + ' KB' : (size / 1024000).toFixed(2) + ' MB';
    }

    private ridStr(v: any): string {
        if (v == null) return '';
        if (wjcCore.isObject(v) && v['@rid']) return v['@rid'];
        return String(v);
    }

    private saveBlob(blob: Blob, name: string): void {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }

    download(row: LedgerRow): void {
        if (!row.fileName) {
            return;
        }
        this.http.get(this.base + '/download', {params: {uuid: row.uuid, file: row.fileName}, responseType: 'blob'}).subscribe({
            next: (blob: Blob) => this.saveBlob(blob, row.fileName),
            error: () => this.toasterService.pop('error', this.transloco.translate('kobeac.trade_ledger'), this.transloco.translate('kobeac.download_failed')),
        });
    }

    downloadSelected(): void {
        const items = this.rows.filter((r) => r.checked && r.fileName).map((r) => ({uuid: r.uuid, file: r.fileName}));
        if (items.length === 0) {
            this.toasterService.pop('warning', this.transloco.translate('kobeac.trade_ledger'), this.transloco.translate('kobeac.select_file'));
            return;
        }
        this.http.post(this.base + '/download-zip', {items: items}, {responseType: 'blob'}).subscribe({
            next: (blob: Blob) => this.saveBlob(blob, 'baibaidaicho.zip'),
            error: () => this.toasterService.pop('error', this.transloco.translate('kobeac.trade_ledger'), this.transloco.translate('kobeac.zip_download_failed')),
        });
    }

    generate(): void {
        if (!this.genAuctionRid) {
            this.toasterService.pop('warning', this.transloco.translate('kobeac.trade_ledger'), this.transloco.translate('kobeac.select_auction'));
            return;
        }
        this.generating = true;
        this.http.post(this.base + '/generate', {auction: this.genAuctionRid, createdDate: this.genCreatedDate}).subscribe({
            next: (res: any) => {
                this.generating = false;
                this.toasterService.pop('info', this.transloco.translate('kobeac.trade_ledger'), this.transloco.translate('kobeac.generated_n', {count: res && res.generated}));
                this.list();
            },
            error: () => {
                this.generating = false;
                this.toasterService.pop('error', this.transloco.translate('kobeac.trade_ledger'), this.transloco.translate('kobeac.generate_failed'));
            },
        });
    }
}
